import React, { useState } from "react";
import { Alert, ButtonGroup, Dropdown, DropdownButton, Form } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { useRouteLoaderData } from "react-router-dom";

import { useAppApi, useAppContext } from "@/App";
import { GameType } from "@/const";
import { useRequest } from "@components/AppHook";
import { DevForm, FormFieldGroup } from "@components/Forms/Fields";
import { DevModal } from "@components/Helpers";
import { DefaultLayout } from "@components/Layouts";
import { MarketOrderTable } from "@components/MarketOrder";
import { ListingCard } from "@components/Pages";
import { parseQS, url_reverse } from "@components/Utils";

export default function MarketOrderScreen() {
    return (
        <DefaultLayout>
            <List />
        </DefaultLayout>
    );
}

function List() {
    const { t } = useTranslation();
    const { apiData, setApiData, apiUrl } = useAppApi(url_reverse("api:market:order:list"));
    const { setDelData } = useAppContext();
    const [markAsRefund, setMarkAsRefund] = useState({});
    const [fakePayment, setFakePayment] = useState({});
    const [changeOrderData, setChangeOrderData] = useState({});
    const { api_post, api_get } = useRequest();
    const commonInfo = useRouteLoaderData("commonInfo");

    if (!apiData) return;

    const markAsRefundUpdate = (k, v) => {
        const newMarkAsRefund = { ...markAsRefund };
        if (v === null) {
            delete newMarkAsRefund[k];
        } else {
            newMarkAsRefund[k] = v;
        }
        setMarkAsRefund(newMarkAsRefund);
    };

    const changeOrderDataUpdate = (k, v) => {
        const newChangeOrderData = { ...changeOrderData };
        if (v === null) {
            delete newChangeOrderData[k];
        } else {
            newChangeOrderData[k] = v;
        }
        setChangeOrderData(newChangeOrderData);
    };

    const markAsRefundShow = (ids) => {
        api_get(null, url_reverse("api:market:order:check_refund_info", null, { ids }), {
            then: (resp) => {
                const newMarkAsRefund = { ...markAsRefund };
                newMarkAsRefund.failed = resp.failed;
                newMarkAsRefund.old = resp.old;
                newMarkAsRefund.badonline = resp.badonline;
                newMarkAsRefund.non_refundable = resp.non_refundable;
                newMarkAsRefund.ids = ids;
                newMarkAsRefund.code = resp.code;
                setMarkAsRefund(newMarkAsRefund);
            },
        });
    };

    const changeOrderDataShow = (ids) => {
        const newOrderData = { ...changeOrderData };
        newOrderData.ids = ids;
        setChangeOrderData(newOrderData);
    };

    const markAsRefundClose = () => {
        markAsRefundUpdate("ids", null);
    };

    const fakePaymentClose = () => {
        setFakePayment({ ...fakePayment, ids: null, dblConfirm: null });
    };

    const changeOrderDataClose = () => {
        changeOrderDataUpdate("ids", null);
        setChangeOrderData({});
        const newApiData = { ...apiData };
        newApiData.transfer_error = null;
        setApiData(newApiData);
    };

    const markAsRefundSubmit = (ev) => {
        ev.preventDefault();

        api_post(ev, apiUrl, {
            params: {
                action: "mark_as_refund",
                ids: markAsRefund.ids,
                comment: markAsRefund.comment,
                force: markAsRefund.force,
            },
            then: (resp) => {
                if (resp.status === "ok") {
                    const newApiData = { ...apiData };
                    newApiData.object_list = newApiData.object_list.filter((o) => !resp.ids.includes(o.id));
                    setApiData(newApiData);
                    markAsRefundClose();
                }
            },
        });
    };

    const fakePaymentSubmit = (ev) => {
        ev.preventDefault();

        api_post(ev, apiUrl, {
            params: {
                action: "fake_payment",
                ids: fakePayment.ids,
                comment: fakePayment.comment,
            },
            then: (resp) => {
                if (resp.status === "ok") {
                    fakePaymentClose();
                    api_get(ev, apiUrl + window.location.search, {
                        then: (resp2) => setApiData(resp2),
                    });
                }
            },
        });
    };

    const changeOrderDataSubmit = (ev) => {
        ev.preventDefault();

        api_post(ev, apiUrl, {
            params: {
                action: "change_order_email_user_id",
                ids: changeOrderData.ids,
                comment: changeOrderData.comment,
                email: changeOrderData.email,
                user_id: changeOrderData.user_id,
            },
            then: (resp) => {
                if (resp.status === "ok") {
                    changeOrderDataClose();
                    setApiData(resp);
                    setChangeOrderData({});
                } else if (resp.status === "fail") {
                    setApiData((prev) => ({ ...prev, transfer_error: resp.error }));
                }
            },
        });
    };

    const headerTools = (
        <>
            <DropdownButton as={ButtonGroup} size="sm" variant="dark" title={t("С выбранными")}>
                <Dropdown.Item
                    onClick={() => {
                        const ids = apiData.object_list.filter((o) => o.chosen).map((o) => o.id);
                        if (ids.length) {
                            setDelData({
                                url: url_reverse("api:market:order:list", null, { ids }),
                                filterHandler: (resp) => {
                                    return apiData.object_list.filter((o) => !resp.ids.includes(o.id));
                                },
                            });
                        }
                    }}
                >
                    {t("Удалить")}
                </Dropdown.Item>
                {commonInfo.account?.support && (
                    <Dropdown.Item
                        onClick={() => {
                            const ids = apiData.object_list.filter((o) => o.chosen).map((o) => o.id);
                            if (ids.length) {
                                markAsRefundShow(ids);
                            }
                        }}
                    >
                        {t("Сделать возврат")}
                    </Dropdown.Item>
                )}
                <Dropdown.Item
                    onClick={() => {
                        const hasCodes = apiData.object_list
                            .filter((o) => o.chosen)
                            .filter((o) => o.game.type === GameType.CODE)
                            .map((o) => o.id);
                        const ids = apiData.object_list.filter((o) => o.chosen).map((o) => o.id);
                        if (ids.length) {
                            setFakePayment({ ...fakePayment, ids, dblConfirm: Boolean(hasCodes.length) });
                        }
                    }}
                >
                    {t("Эмулировать")}
                </Dropdown.Item>
                <Dropdown.Item
                    onClick={() => {
                        const ids = apiData.object_list.filter((o) => o.chosen).map((o) => o.id);
                        if (ids.length) {
                            changeOrderDataShow(ids);
                        }
                    }}
                >
                    {t("Перенести")}
                </Dropdown.Item>
            </DropdownButton>{" "}
            <DevForm.Button
                variant="success"
                size="sm"
                name="export_market_users"
                onClick={(ev) =>
                    api_post(ev, apiUrl, {
                        params: { ...parseQS(), action: "export_market_users" },
                    })
                }
            >
                {t("Экспорт пользователей")}
            </DevForm.Button>
        </>
    );

    return (
        <ListingCard header={t("Заказы")} headerTools={headerTools}>
            <DevModal
                size="lg"
                show={markAsRefund.ids}
                onHide={markAsRefundClose}
                header={t("Сделать возврат")}
                body={
                    <>
                        {markAsRefund.code?.length > 0 && (
                            <Alert variant="danger">
                                {t(
                                    "Предупреждение: вы собираетесь оформить возврат денежных средств за покупку игры с типом 'Пин-код' — это серьезная операция, которая требует обдуманного решения. Данная операция может быть выполнена только с разрешения вашего непосредственного руководителя. Обратите внимание, что возврат средств может быть невозможен в зависимости от условий продажи, и вы несете полную ответственность за последствия. Если необходимо протестировать процесс возврата, используйте тестовые проекты, где это разрешено. Для получения информации о тестовых проектах обратитесь к своему руководителю или автору задачи."
                                )}
                            </Alert>
                        )}
                        {markAsRefund.old?.length +
                            markAsRefund.badonline?.length +
                            markAsRefund.failed?.length +
                            markAsRefund.non_refundable?.length +
                            markAsRefund.code?.length >
                            0 && (
                            <Alert variant="danger">
                                {markAsRefund.code?.length > 0 && (
                                    <div>
                                        {t("Пин-Код")}: {markAsRefund.code.join(", ")}
                                    </div>
                                )}
                                {markAsRefund.old?.length > 0 && (
                                    <div>
                                        {t("Старые")}: {markAsRefund.old.join(", ")}
                                    </div>
                                )}
                                {markAsRefund.badonline?.length > 0 && (
                                    <div>
                                        {t("Онлайн")}: {markAsRefund.badonline.join(", ")}
                                    </div>
                                )}
                                {markAsRefund.failed?.length > 0 && (
                                    <div>
                                        {t("Ошибка")}: {markAsRefund.failed.join(", ")}
                                    </div>
                                )}
                                {markAsRefund.non_refundable?.length > 0 && (
                                    <div>
                                        {t("Невозвратные")}: {markAsRefund.non_refundable.join(", ")}
                                    </div>
                                )}
                            </Alert>
                        )}
                        <FormFieldGroup label={t("Комментарий к возврату денег")} required={true}>
                            <DevForm.TextArea
                                name="comment"
                                value={markAsRefund.comment}
                                required={true}
                                onChange={(ev) => markAsRefundUpdate("comment", ev.target.value)}
                            />
                        </FormFieldGroup>
                        <FormFieldGroup>
                            <DevForm.Checkbox
                                label={t("Игнорировать активации")}
                                onChange={(ev) => {
                                    markAsRefundUpdate("force", ev.target.checked);
                                }}
                                checked={markAsRefund.force}
                            />
                        </FormFieldGroup>
                    </>
                }
                footer={
                    <DevForm.Button variant="primary" type="submit" name="mark_as_refund">
                        {t("Сделать возврат")}
                    </DevForm.Button>
                }
                renderWrapper={(children) => <Form onSubmit={markAsRefundSubmit}>{children}</Form>}
            />

            <DevModal
                size="lg"
                show={Boolean(fakePayment.ids) && Boolean(fakePayment.dblConfirm)}
                onHide={fakePaymentClose}
                header={t("Эмуляция покупки")}
                body={
                    <span style={{ color: "red" }}>
                        <Trans>
                            Предупреждение: вы собираетесь проэмулировать покупку игры с типом 'Пин-код' — это серьезная
                            операция, которая требует обдуманного решения. Данная операция может быть выполнена только с
                            разрешения вашего непосредственного руководителя. Обратите внимание, что возврат средств за
                            эмуляцию невозможен, и вы несете полную ответственность за последствия. Если необходимо
                            протестировать покупку, используйте тестовые проекты, где это разрешено. Для получения
                            информации о тестовых проектах обратитесь к своему руководителю или автору задачи.
                        </Trans>
                    </span>
                }
                footer={
                    <DevForm.Button
                        variant="primary"
                        name="emulation_confirm"
                        onClick={() => setFakePayment({ ...fakePayment, dblConfirm: false })}
                    >
                        {t("Продолжить")}
                    </DevForm.Button>
                }
            />
            <DevModal
                size="lg"
                show={Boolean(fakePayment.ids) && !fakePayment.dblConfirm}
                onHide={fakePaymentClose}
                header={t("Эмулировать")}
                body={
                    <FormFieldGroup label={t("Комментарий к эмуляции")}>
                        <DevForm.TextArea
                            name="comment"
                            value={fakePayment.comment}
                            onChange={(ev) => setFakePayment({ ...fakePayment, comment: ev.target.value })}
                        />
                    </FormFieldGroup>
                }
                footer={
                    <DevForm.Button variant="primary" type="submit" name="fake_payment">
                        {t("Эмулировать")}
                    </DevForm.Button>
                }
                renderWrapper={(children) => <Form onSubmit={fakePaymentSubmit}>{children}</Form>}
            />

            <DevModal
                size="lg"
                show={changeOrderData.ids}
                onHide={changeOrderDataClose}
                header={t("Перенести покупку")}
                body={
                    <>
                        {apiData?.transfer_error && (
                            <div className="dmg-callout dmg-callout-danger mb-3">
                                <div dangerouslySetInnerHTML={{ __html: apiData.transfer_error }} />
                            </div>
                        )}
                        {changeOrderData?.ids?.length !== 1 && (
                            <Alert variant="danger">
                                <div>{t("Необходимо выбрать только один заказ")}</div>
                            </Alert>
                        )}
                        <FormFieldGroup label={t("Новый email")} required={true}>
                            <DevForm.TextInput
                                name="email"
                                value={changeOrderData.email}
                                required={true}
                                onChange={(ev) => changeOrderDataUpdate("email", ev.target.value)}
                            />
                        </FormFieldGroup>
                        <FormFieldGroup label={t("Новый user_id")} required={true}>
                            <DevForm.TextInput
                                name="user_id"
                                value={changeOrderData.user_id}
                                required={true}
                                onChange={(ev) => changeOrderDataUpdate("user_id", ev.target.value)}
                                type="number"
                            />
                        </FormFieldGroup>
                        <FormFieldGroup label={t("Причина переноса")} required={true}>
                            <DevForm.TextInput
                                name="comment"
                                value={changeOrderData.comment}
                                required={true}
                                onChange={(ev) => changeOrderDataUpdate("comment", ev.target.value)}
                            />
                        </FormFieldGroup>
                    </>
                }
                footer={
                    changeOrderData?.ids?.length === 1 && (
                        <DevForm.Button variant="primary" type="submit" name="change_order_data">
                            {t("Перенести")}
                        </DevForm.Button>
                    )
                }
                renderWrapper={(children) => <Form onSubmit={changeOrderDataSubmit}>{children}</Form>}
            />

            <MarketOrderTable selectable={true} refundColumn={true} />
        </ListingCard>
    );
}
